import {
  handleFreeFormInput,
  setFreeFormQuery,
} from 'actions/workflows-v2/workflowsV2';
import { setSelectedIntentIndex } from 'actions/workflows/workflows';
import { isSelectIntentComponent } from 'api/types';
import type React from 'react';
import {
  selectFreeFormInputPlaceholder,
  selectFreeFormQuery,
  selectIsSubmitDisabledBySignInStep,
  selectLastWidgetComponent,
  selectLoading,
  selectWorkflowsConfig,
} from 'reducers/workflows';
import { useAppDispatch, useAppSelector } from '../hooks';
import FormWrapper from './FormWrapper/IntentFormWrapper';
import { TextInput } from 'components/TextInput/TextInput';

interface FreeFormInputProps {
  className?: string;
  isDelaying: boolean;
}

export const FreeFormInput: React.FC<FreeFormInputProps> = ({
  className,
  isDelaying,
}) => {
  const dispatch = useAppDispatch();

  const workflowConfig = useAppSelector(selectWorkflowsConfig);
  const placeholder = useAppSelector(selectFreeFormInputPlaceholder);
  const query = useAppSelector(selectFreeFormQuery);
  const lastWidgetComponent = useAppSelector(selectLastWidgetComponent);
  const loading = useAppSelector(selectLoading);
  const isSubmitDisabledBySignInStep = useAppSelector(
    selectIsSubmitDisabledBySignInStep,
  );

  const isQueryEmpty = query.trim() === '';
  const isSubmitDisabled =
    isQueryEmpty || loading || isSubmitDisabledBySignInStep || isDelaying;

  const isLastComponentSelectIntentComponent =
    lastWidgetComponent && isSelectIntentComponent(lastWidgetComponent);

  function handleSubmit(event: React.SyntheticEvent) {
    event.preventDefault();
    dispatch(handleFreeFormInput());
  }

  // A workaround to prevent free-form input height flashing:
  const rowsProps = isQueryEmpty ? { rows: 1 } : { maxRows: 10 };

  return (
    <FormWrapper
      className={className}
      formTitle=''
      isDisabled={isSubmitDisabled}
      isForm
      isLastStep
      onSubmit={handleSubmit}
      stepIndex={-1}
    >
      <TextInput
        aria-label={placeholder}
        dir='auto'
        disableSendMessage={isSubmitDisabled}
        InputProps={{
          inputProps: {
            className: 'forethought-scrollbar',
            'data-testid': 'free-form-input',
          },
        }}
        multiline
        onChange={e => {
          dispatch(setFreeFormQuery(e.target.value));
        }}
        onFocus={() => {
          if (!isLastComponentSelectIntentComponent) {
            return;
          }

          const intents = lastWidgetComponent.component_fields
            .show_top_n_intents
            ? workflowConfig?.top_n_intents
            : [];

          if (intents?.length) {
            dispatch(setSelectedIntentIndex(intents.length));
          }
        }}
        onSendMessage={handleSubmit}
        placeholder={placeholder}
        value={query}
        {...rowsProps}
      />
    </FormWrapper>
  );
};
